import { Alert } from '@/components/ui/alert';
import type { DisplayRightsLaunchGate } from '@/components/display-rights/display-rights-launch-gate';
import type { DisplayRightsMetaState } from '@/components/display-rights/display-rights-status';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  FormatNumber,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  MentionBoostPlacementMode,
  MentionBoostSetupAudienceMode,
  type DisplayRightsProposalSummary,
  type MentionBoostTargetAgeRangeDto,
  type MentionBoostTargetLocationDto,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { MentionBoostLaunchGateChecklist } from './MentionBoostLaunchGateChecklist';

// Destination + UTM summary frozen at launch, shown as a compact recap line.
export interface LaunchDestinationSummary {
  // Short host/path label of the destination link.
  label: string;
  // Number of UTM pairs actually applied (0 hides the UTM sub-line).
  utmAppliedCount: number;
  // Whether the applied UTM is the untouched Fluenceur convention (vs. custom).
  utmIsConvention: boolean;
}

/** Financial and placement facts frozen into the explicit launch decision. */
export interface LaunchConfigurationSummary {
  billedAccount: {
    id: string;
    name: string;
    currencyCode: string | null;
  } | null;
  budgetAmountInCents: number;
  budgetCurrencyCode: string;
  durationDays: number;
  estimatedEndDate: string;
  placementMode: MentionBoostPlacementMode;
  placementCampaignName: string | null;
  campaignBudgetManaged: boolean;
  audienceMode: MentionBoostSetupAudienceMode;
  targetLocations: MentionBoostTargetLocationDto[];
  targetAgeRange: MentionBoostTargetAgeRangeDto;
}

interface MentionBoostLaunchConfirmDialogProps {
  isOpen: boolean;
  submitting: boolean;
  // Guards the confirm action: if the boost stops being launchable while the
  // dialog is open (e.g. a background eligibility change), the confirm button
  // disables instead of silently no-opping on click.
  canConfirm: boolean;
  // Compact destination + UTM recap. Null when there is no resolved destination.
  destination?: LaunchDestinationSummary | null;
  configuration?: LaunchConfigurationSummary | null;
  // The double validation. Absent (or not applying) on a boost nobody attached
  // compensation to, which is then confirmed exactly as it always was.
  gate?: DisplayRightsLaunchGate;
  displayRights?: DisplayRightsProposalSummary | null;
  metaState?: DisplayRightsMetaState;
  offPlatformAttestation?: boolean;
  onOffPlatformAttestationChange?: (value: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Explicit-confirm dialog for launching a prepared boost. Launch is never
 * one-click: the marketer must confirm here before the boost is submitted to
 * the provider. Mirrors the authorization preflight dialog so the launched and
 * preflight surfaces stay visually consistent. The ad account to bill is chosen
 * earlier, in the setup form, so this dialog stays a pure confirmation.
 */
export function MentionBoostLaunchConfirmDialog({
  isOpen,
  submitting,
  canConfirm,
  destination,
  configuration = null,
  gate,
  displayRights = null,
  metaState,
  offPlatformAttestation = false,
  onOffPlatformAttestationChange,
  onClose,
  onConfirm,
}: MentionBoostLaunchConfirmDialogProps) {
  const { t } = useTranslation();

  const utmRecap =
    destination && destination.utmAppliedCount > 0
      ? t(
          destination.utmIsConvention
            ? 'mentions.boost.launch.recapUtmConvention'
            : 'mentions.boost.launch.recapUtmCustom',
          { count: destination.utmAppliedCount }
        )
      : null;
  const audienceSummary = configuration
    ? t(
        configuration.audienceMode === MentionBoostSetupAudienceMode.ADVANTAGE_PLUS
          ? 'mentions.boost.launch.recapAudienceAdvantagePlus'
          : 'mentions.boost.launch.recapAudienceManual',
        {
          locations: configuration.targetLocations.map((location) => location.name).join(', '),
          min: configuration.targetAgeRange.min,
          max: configuration.targetAgeRange.max,
        }
      )
    : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={({ open }) => !open && onClose()} placement="center">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="480px"
            maxH="calc(100dvh - 2rem)"
            rounded="xl"
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header flexShrink={0}>
              <Dialog.Title>{t('mentions.boost.launch.confirmTitle')}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton aria-label={t('mentions.boost.launch.close')} size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body overflowY="auto" flex="1">
              <VStack align="stretch" gap={4}>
                <Text fontSize="sm" color="gray.700">
                  {t('mentions.boost.launch.confirmDescription')}
                </Text>
                {destination && (
                  <Box
                    border="1px solid"
                    borderColor="border.muted"
                    borderRadius="md"
                    px={3}
                    py={2.5}
                  >
                    <HStack justify="space-between" gap={3} align="baseline">
                      <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                        {t('mentions.boost.launch.recapDestinationLabel')}
                      </Text>
                      <Text fontSize="xs" fontFamily="mono" textAlign="right" wordBreak="break-all">
                        {destination.label}
                      </Text>
                    </HStack>
                    {utmRecap && (
                      <Text fontSize="2xs" color="fg.muted" mt={1} textAlign="right">
                        {utmRecap}
                      </Text>
                    )}
                  </Box>
                )}
                {configuration && (
                  <Box
                    border="1px solid"
                    borderColor="border.muted"
                    borderRadius="md"
                    px={3}
                    py={2.5}
                  >
                    <VStack align="stretch" gap={2}>
                      <RecapRow
                        label={t('mentions.boost.launch.recapAccountLabel')}
                        value={
                          configuration.billedAccount
                            ? `${configuration.billedAccount.name} · ${configuration.billedAccount.id}${
                                configuration.billedAccount.currencyCode
                                  ? ` · ${configuration.billedAccount.currencyCode}`
                                  : ''
                              }`
                            : t('mentions.boost.launch.recapAccountPending')
                        }
                      />
                      {configuration.campaignBudgetManaged ? (
                        <RecapRow
                          label={t('mentions.boost.launch.recapBudgetLabel')}
                          value={t('mentions.boost.launch.recapCampaignBudget', {
                            campaign:
                              configuration.placementCampaignName ??
                              t('mentions.boost.launch.recapExistingCampaign'),
                          })}
                        />
                      ) : (
                        <RecapRow
                          label={t('mentions.boost.launch.recapBudgetLabel')}
                          value={
                            <FormatNumber
                              value={configuration.budgetAmountInCents / 100}
                              style="currency"
                              currency={configuration.budgetCurrencyCode}
                            />
                          }
                        />
                      )}
                      <RecapRow
                        label={t('mentions.boost.launch.recapDurationLabel')}
                        value={t('mentions.boost.launch.recapDurationValue', {
                          count: configuration.durationDays,
                          endDate: configuration.estimatedEndDate,
                        })}
                      />
                      <RecapRow
                        label={t('mentions.boost.launch.recapPlacementLabel')}
                        value={
                          configuration.placementMode ===
                          MentionBoostPlacementMode.EXISTING_CAMPAIGN
                            ? t('mentions.boost.launch.recapExistingCampaign', {
                                campaign: configuration.placementCampaignName ?? '',
                              })
                            : t('mentions.boost.launch.recapNewCampaign')
                        }
                      />
                      <RecapRow
                        label={t('mentions.boost.launch.recapAudienceLabel')}
                        value={audienceSummary ?? ''}
                      />
                    </VStack>
                  </Box>
                )}
                {gate && metaState && onOffPlatformAttestationChange && (
                  <MentionBoostLaunchGateChecklist
                    gate={gate}
                    proposal={displayRights}
                    metaState={metaState}
                    offPlatformAttestation={offPlatformAttestation}
                    onOffPlatformAttestationChange={onOffPlatformAttestationChange}
                  />
                )}
                <Alert
                  status="info"
                  variant="surface"
                  title={t('mentions.boost.launch.confirmNextTitle')}
                >
                  {t('mentions.boost.launch.confirmNextDescription')}
                </Alert>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer flexShrink={0}>
              <Button variant="outline" onClick={onClose}>
                {t('common.close')}
              </Button>
              <Button
                colorPalette="blue"
                loading={submitting}
                disabled={!canConfirm}
                onClick={onConfirm}
              >
                {t('mentions.boost.launch.confirmCta')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function RecapRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <HStack justify="space-between" gap={3} align="baseline">
      <Text fontSize="xs" color="fg.muted" flexShrink={0}>
        {label}
      </Text>
      <Text fontSize="xs" textAlign="right" wordBreak="break-word">
        {value}
      </Text>
    </HStack>
  );
}
